import { firstQueryValue, parseCivilDate } from '@/shared/lib';
import type { SortOrder } from '@/shared/lib';
import type { MatrixModelSortField } from '../api/matrix-models.api';

export const MODEL_SORT_FIELDS: readonly MatrixModelSortField[] = ['createdAt', 'title'];

export interface ModelsListFilters {
  createdAt?: string;
  categoryId?: number;
  createdBy?: number;
}

/** An empty sort means the endpoint default, "Mais recente". */
export interface ModelsListSort {
  sort: MatrixModelSortField | '';
  order: SortOrder | '';
}

const CIVIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isModelSortField = (value: unknown): value is MatrixModelSortField =>
  MODEL_SORT_FIELDS.includes(value as MatrixModelSortField);

const isSortOrder = (value: unknown): value is SortOrder => value === 'asc' || value === 'desc';

export const toModelsListSort = (sort: unknown, order: unknown): ModelsListSort =>
  isModelSortField(sort) && isSortOrder(order) ? { sort, order } : { sort: '', order: '' };

export const parseModelsListSort = (query: Record<string, unknown>): ModelsListSort =>
  toModelsListSort(firstQueryValue(query.sort), firstQueryValue(query.order));

export const parseCivilDateQuery = (raw: unknown): string | undefined => {
  const value = firstQueryValue(raw);
  return value && CIVIL_DATE_PATTERN.test(value) && parseCivilDate(value) ? value : undefined;
};

export const parsePositiveIntegerQuery = (raw: unknown): number | undefined => {
  const value = Number(firstQueryValue(raw));
  return Number.isInteger(value) && value > 0 ? value : undefined;
};

export const parseModelsListFilters = (query: Record<string, unknown>): ModelsListFilters => ({
  createdAt: parseCivilDateQuery(query.createdAt),
  categoryId: parsePositiveIntegerQuery(query.categoryId),
  createdBy: parsePositiveIntegerQuery(query.createdBy),
});

export const countActiveModelFilters = (filters: ModelsListFilters): number =>
  [filters.createdAt, filters.categoryId, filters.createdBy].filter((value) => value !== undefined).length;

export const buildModelsListUrlQuery = (
  { sort, order }: ModelsListSort,
  filters: ModelsListFilters
): Record<string, string | number> => ({
  ...(sort && order ? { sort, order } : {}),
  ...(filters.createdAt ? { createdAt: filters.createdAt } : {}),
  ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
  ...(filters.createdBy ? { createdBy: filters.createdBy } : {}),
});
